<template><div><code-well-header>
  <dt-button v-dt-tooltip="`Simple tooltip`">Hover me</dt-button>
</code-well-header>
<h2 id="tooltip-as-a-directive" tabindex="-1" class="d-docsite--header-2">Tooltip as a Directive <a class="header-anchor d-link d-docsite--link d-link" href="#tooltip-as-a-directive" aria-hidden="true">#</a></h2>
<h3 id="usage" tabindex="-1" class="d-docsite--header-3">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h3>
<h4 class="d-docsite--header-4">Default</h4>
<p class="d-docsite--paragraph">Default tooltip directive uses top as default placement</p>
<code-well-header class="d-hmn164">
  <dt-button v-dt-tooltip="`Tooltip text`">Hover me</dt-button>
</code-well-header>
<code-example-tabs
htmlCode='
<span data-dt-tooltip-id="dt0" aria-describedby="tippy-1">Span with tooltip</span>
<div data-tippy-root="" id="tippy-1" style="pointer-events: none; z-index: 400; visibility: visible; position: absolute; inset: auto auto 0px 0px; margin: 0px; transform: translate3d(64.5px, -378px, 0px);">
  <div class="tippy-box" data-state="visible" tabindex="-1" data-animation="fade" role="tooltip" data-placement="top" style="max-width: 350px; transition-duration: 180ms;">
    <div class="tippy-content" data-state="visible" style="transition-duration: 180ms;"><div id="dt4" class="d-tooltip">Default placement</div></div>
    <div class="tippy-svg-arrow" style="position: absolute; left: 0px; transform: translate3d(59px, 0px, 0px);">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="7"><path d="M 14.5,7 8,0 1.5,7 Z"></path></svg>
    </div>
  </div>
</div>
'
vueCode='
<dt-button v-dt-tooltip="`Tooltip text`">Hover me</dt-button>
'
showHtmlWarning />
<h4 class="d-docsite--header-4">With Placement</h4>
<p class="d-docsite--paragraph">It's possible to change the tooltip default placement with directive arguments, possible values: bottom, bottom-start, bottom-end, right, right-start, right-end, left, left-start, left-end, top, top-start, top-end.</p>
<code-well-header class='d-hmn164'>
<dt-button v-dt-tooltip:bottom-start="`Tooltip text`">Placeholder Button</dt-button>
</code-well-header>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token operator">&lt;</span>dt<span class="token operator">-</span>button v<span class="token operator">-</span>dt<span class="token operator">-</span>tooltip<span class="token operator">:</span>bottom<span class="token operator">-</span>start<span class="token operator">=</span><span class="token string">"`Tooltip text`"</span><span class="token operator">></span>Placeholder Button<span class="token operator">&lt;</span><span class="token operator">/</span>dt<span class="token operator">-</span>button<span class="token operator">></span></span>
<span class="line"></span></code></pre>
</div><h4 class="d-docsite--header-4">With Object Syntax</h4>
<p class="d-docsite--paragraph">It's possible to change any property of the tooltip with object syntax.</p>
<code-well-header class='d-hmn164'>
<dt-button v-dt-tooltip="{ message: 'Tooltip text', placement: 'bottom-start', delay: false }">Placeholder Button</dt-button>
</code-well-header>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token operator">&lt;</span>dt<span class="token operator">-</span>button v<span class="token operator">-</span>dt<span class="token operator">-</span>tooltip<span class="token operator">=</span><span class="token string">"{ message: 'Tooltip text', placement: 'bottom-start', delay: false }"</span><span class="token operator">></span>Placeholder Button<span class="token operator">&lt;</span><span class="token operator">/</span>dt<span class="token operator">-</span>button<span class="token operator">></span></span>
<span class="line"></span></code></pre>
</div><h3 id="import" tabindex="-1" class="d-docsite--header-3">Import <a class="header-anchor d-link d-docsite--link d-link" href="#import" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Import the directive from dialtone-vue</p>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token keyword">import</span> <span class="token punctuation">{</span> DtTooltipDirective <span class="token punctuation">}</span> <span class="token keyword">from</span> <span class="token string">"@dialpad/dialtone-vue"</span><span class="token punctuation">;</span></span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph">Install the directive into vue instance</p>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line">Vue<span class="token punctuation">.</span><span class="token function">use</span><span class="token punctuation">(</span>DtTooltipDirective<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line"></span></code></pre>
</div><h2 id="tooltip-as-a-component" tabindex="-1" class="d-docsite--header-2">Tooltip as a Component <a class="header-anchor d-link d-docsite--link d-link" href="#tooltip-as-a-component" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The tooltip, also known as infotip or hint, is a common graphical user interface element in which, when hovering over a
screen element or component, a text box displays information about that element (such as a description of a button's
function, or what an abbreviation stands for). The tooltip is displayed continuously as long as the user hovers over the
element.</p>
<p class="d-docsite--paragraph">When placing DtTooltip on a disabled DtButton, wrap the button in a <code v-pre>&lt;span&gt;</code> element — disabled elements do not fire mouse events, so the tooltip anchor must be on the wrapper, not on the disabled button itself.</p>
<p class="d-docsite--paragraph">A tooltip has two slots:</p>
<ol class="d-docsite--ordered-list">
<li class="d-docsite--list-element"><strong>the anchor</strong> required slot</li>
<li class="d-docsite--list-element"><strong>the default</strong> slot (which could be replaced with prop message)</li>
</ol>
<h3 id="base-styles" tabindex="-1" class="d-docsite--header-3">Base Styles <a class="header-anchor d-link d-docsite--link d-link" href="#base-styles" aria-hidden="true">#</a></h3>
<code-well-header class="d-hmn164">
  <dt-tooltip message="tooltip">
    <template #anchor>
      <dt-button>
        Hover me
      </dt-button>
    </template>
  </dt-tooltip>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <span>
    <button type="button" class="base-button__button d-btn d-btn--primary" aria-describedby="tippy-1">
      <span class="d-btn__label base-button__label"> Hover to show tooltip </span>
    </button>
  </span>
</div>
<div data-tippy-root="" id="tippy-1" style="pointer-events: none; z-index: 400; visibility: visible; position: absolute; inset: auto auto 0px 0px; margin: 0px; transform: translate3d(129px, -322px, 0px);">
  <div class="tippy-box" data-state="visible" tabindex="-1" data-animation="fade" role="tooltip" data-placement="top" data-theme="inverted" style="max-width: 350px; transition-duration: 180ms;">
    <div class="tippy-content" data-state="visible" style="transition-duration: 180ms;"><div id="dt0" class="d-tooltip">tooltip</div></div>
    <div class="tippy-svg-arrow" style="position: absolute; left: 0px; transform: translate3d(23.5px, 0px, 0px);">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="7"><path d="M 14.5,7 8,0 1.5,7 Z"></path></svg>
    </div>
  </div>
</div>
'
vueCode='
<dt-tooltip message="tooltip">
  <template #anchor>
    <dt-button>
      Hover me
    </dt-button>
  </template>
</dt-tooltip>
'
showHtmlWarning />
<h3 id="inverted" tabindex="-1" class="d-docsite--header-3">Inverted <a class="header-anchor d-link d-docsite--link d-link" href="#inverted" aria-hidden="true">#</a></h3>
<code-well-header bgclass="d-bgc-contrast" class="d-hmn164">
  <dt-tooltip inverted message="tooltip">
    <template #anchor>
      <dt-button>
        Hover me
      </dt-button>
    </template>
  </dt-tooltip>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <span>
    <button type="button" class="base-button__button d-btn d-btn--primary" aria-describedby="tippy-1">
      <span class="d-btn__label base-button__label"> Hover to show tooltip </span>
    </button>
  </span>
</div>
<div data-tippy-root="" id="tippy-1" style="pointer-events: none; z-index: 400; visibility: visible; position: absolute; inset: auto auto 0px 0px; margin: 0px; transform: translate3d(129px, -322px, 0px);">
  <div class="tippy-box" data-state="visible" tabindex="-1" data-animation="fade" role="tooltip" data-placement="top" data-theme="inverted" style="max-width: 350px; transition-duration: 180ms;">
    <div class="tippy-content" data-state="visible" style="transition-duration: 180ms;"><div id="dt0" class="d-tooltip d-tooltip--inverted">tooltip</div></div>
    <div class="tippy-svg-arrow" style="position: absolute; left: 0px; transform: translate3d(23.5px, 0px, 0px);">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="7"><path d="M 14.5,7 8,0 1.5,7 Z"></path></svg>
    </div>
  </div>
</div>
'
vueCode='
<dt-tooltip inverted message="tooltip">
  <template #anchor>
    <dt-button>
      Hover me
    </dt-button>
  </template>
</dt-tooltip>
'
showHtmlWarning />
<h3 id="placement" tabindex="-1" class="d-docsite--header-3">Placement <a class="header-anchor d-link d-docsite--link d-link" href="#placement" aria-hidden="true">#</a></h3>
<code-well-header>
  <example-tooltip-directions :directions="directions" />
</code-well-header>
<code-example-tabs
vueCode='
<dt-tooltip
  message="This is a simple tooltip. The tooltip can be positioned in different directions."
  :placement="placement"
>
  <template #anchor>
    <dt-button>
      {{ placement }}
    </dt-button>
  </template>
</dt-tooltip>
'
/>
<h3 id="external-anchor" tabindex="-1" class="d-docsite--header-3">External anchor <a class="header-anchor d-link d-docsite--link d-link" href="#external-anchor" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-button
    id="external-tooltip-anchor"
    importance="outlined"
  >
    External anchor
  </dt-button>
  <dt-tooltip
    external-anchor="#external-tooltip-anchor"
  >
    This is a tooltip with external anchor
  </dt-tooltip>
</code-well-header>
<code-example-tabs
vueCode='
<dt-button
  id="external-tooltip-anchor"
  importance="outlined"
>
  External anchor
</dt-button>
<dt-tooltip
  external-anchor="#external-tooltip-anchor"
>
  This is a tooltip with external anchor
</dt-tooltip>
'
/>
<h3 id="fallback-placements" tabindex="-1" class="d-docsite--header-3">Fallback Placements <a class="header-anchor d-link d-docsite--link d-link" href="#fallback-placements" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The tooltip uses <a href="https://atomiks.github.io/tippyjs/v6/headless-tippy/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">headless-tippy</a> and
<a href="https://popper.js.org/docs/v2/modifiers/flip/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">popper</a>, if the tooltip opens in a placement where it will
be clipped, it will move to a new position. It will do this automatically by default, but if you want to
manually specify which position it will move to in what order you can do so via the fallbackPlacements prop.</p>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="tooltip" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="tooltip" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Reads out the tooltip content as a supplementary description for its trigger when the trigger is focused.
See also <a href="https://www.w3.org/TR/wai-aria-practices-1.1/#tooltip" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">wai aria practices 1.1</a>.</p>
<h3 id="anchor" tabindex="-1" class="d-docsite--header-3">Anchor <a class="header-anchor d-link d-docsite--link d-link" href="#anchor" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The anchor element that activates the tooltip should be fully accessible by keyboard. The easiest way to do this is by
using an element like an <code v-pre>DtButton</code> that is already accessible. When pressing the <code v-pre>ESC</code> key in a focused tooltip,
tooltip will be closed.</p>
<p class="d-docsite--paragraph">There are some required ARIA attributes for the anchor element (such as <code v-pre>aria-hidden</code> set based on <code v-pre>open</code>).
To make this as straightforward as possible, these ARIA attributes are passed
with the correct values as the <code v-pre>attrs</code> to the anchor slot. Applying them is as simple as using <code v-pre>v-bind</code>.</p>
<h3 id="focus-keyboard" tabindex="-1" class="d-docsite--header-3">Focus &amp; Keyboard <a class="header-anchor d-link d-docsite--link d-link" href="#focus-keyboard" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Due to the different contexts in which a tooltip can be used, focus management and
keyboard shortcut <code v-pre>ESC</code> is provided.
You are encouraged to consult the ARIA documentation for the particular role.</p>
<component-accessible-table component-name="tooltip" /></div></template>


<script setup>
import { inject } from 'vue';
import ExampleTooltipDirections from '@exampleComponents/ExampleTooltipDirections.vue';

const dialtoneConstants = inject('dialtoneConstants', {});
const directions = dialtoneConstants.TOOLTIP_DIRECTIONS;
</script>